/**
 * Club Home — Phase 3
 *
 * Route: /club-home?orgId=<uuid>  (orgId optional — falls back to resolveClubOwnerOrg)
 *
 * The primary landing surface for club owners and admins after completing setup.
 *
 * Sections:
 *   A — Header: org name, org type badge, user's role
 *   B — Org Strength Score: avg strength, eligible member count
 *   C — Quick Actions: Invite, Leaderboard, Roster, Setup
 *   D — First Win State: empty (0 members) vs. active team CTA
 *
 * Role gating:
 *   owner / admin  → full access (invite, manage roster, view all)
 *   coach          → read-only (leaderboard + roster view, no invite/manage)
 *   member / null  → access denied, link to club-onboarding
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 *   - organizations table canonical — no gyms dependency.
 */

import { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  SafeAreaView,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { fetchOrganization, getOrgRoleForUser } from '@/lib/organizations';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';
import { fetchOrgStrengthScore } from '@/lib/orgStrength';
import { TYPO } from '@/constants/typography';
import type { Organization, OrgRole, OrgType } from '@/types/database';

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Display label for org type */
const ORG_TYPE_LABELS: Record<OrgType, string> = {
  gym: 'Gym',
  school: 'School',
  team: 'Team',
  company: 'Organization',
  league: 'League',
  federation: 'Federation',
  coaching: 'Coaching', // personal coach org — added by Coach Tier V1
};

/** Emoji for org type badge */
const ORG_TYPE_EMOJI: Record<OrgType, string> = {
  gym: '🏋️',
  school: '🏫',
  team: '⚽',
  company: '🏢',
  league: '🏆',
  federation: '🌐',
  coaching: '🎯', // personal coach org — added by Coach Tier V1
};

/** Display label for user's role */
const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  coach: 'Coach',
  member: 'Member',
};

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ClubHomeScreen() {
  const { orgId: paramOrgId } = useLocalSearchParams<{ orgId?: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;
  const userId = session?.user?.id ?? '';

  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(paramOrgId ?? null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [myRole, setMyRole] = useState<OrgRole | null>(null);
  const [avgStrength, setAvgStrength] = useState<number>(0);
  const [eligibleCount, setEligibleCount] = useState<number>(0);
  const [activeMemberCount, setActiveMemberCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [noOrg, setNoOrg] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setNoOrg(false);

    try {
      // Resolve orgId: prefer route param, fall back to owner org
      let orgId = paramOrgId ?? resolvedOrgId;
      if (!orgId) {
        orgId = await resolveClubOwnerOrg(userId);
        if (orgId) setResolvedOrgId(orgId);
      }

      if (!orgId) {
        setNoOrg(true);
        setLoading(false);
        return;
      }

      // Parallel fetch: org details, role, strength score
      const [fetchedOrg, role, strengthData] = await Promise.all([
        fetchOrganization(orgId),
        getOrgRoleForUser(orgId, userId),
        fetchOrgStrengthScore(orgId),
      ]);

      setOrg(fetchedOrg);
      setMyRole(role);
      setAvgStrength(strengthData.averageStrengthScore);
      setEligibleCount(strengthData.eligibleMemberCount);
      setActiveMemberCount(strengthData.activeMemberCount);
    } finally {
      setLoading(false);
    }
  }, [userId, paramOrgId, resolvedOrgId]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const orgId = paramOrgId ?? resolvedOrgId;
  const isAdminOrOwner = myRole === 'owner' || myRole === 'admin';
  const isCoach = myRole === 'coach';
  const hasAccess = isAdminOrOwner || isCoach;

  const styles = makeStyles(c);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={styles.centered}>
        <ActivityIndicator color={c.accent} />
      </SafeAreaView>
    );
  }

  // ── No org state ────────────────────────────────────────────────────────────
  if (noOrg || !orgId) {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.noOrgEmoji}>🏋️</Text>
        <Text style={styles.heading}>You don't have a club</Text>
        <Text style={styles.subText}>
          Start a club to manage your team, track their strength, and run the leaderboard.
        </Text>
        <Pressable
          style={styles.btnPrimary}
          onPress={() => router.push('/club-onboarding' as any)}
        >
          <Text style={styles.btnPrimaryText}>Start a Club</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  // ── Access denied (member or no role) ──────────────────────────────────────
  if (!hasAccess) {
    return (
      <SafeAreaView style={styles.centered}>
        <Text style={styles.heading}>Access Denied</Text>
        <Text style={styles.subText}>
          Club Home is available to owners, admins, and coaches.
        </Text>
        <Pressable style={styles.btnSecondary} onPress={() => router.back()}>
          <Text style={styles.btnSecondaryText}>Go Back</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  const orgTypeFull = org?.org_type as OrgType ?? 'gym';
  const orgTypeLabel = ORG_TYPE_LABELS[orgTypeFull] ?? 'Organization';
  const orgTypeEmoji = ORG_TYPE_EMOJI[orgTypeFull] ?? '🏢';

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      {/* ── Nav header ── */}
      <View style={styles.navRow}>
        <Pressable onPress={() => router.back()} style={styles.backBtn}>
          <Text style={styles.backBtnText}>← Back</Text>
        </Pressable>
        <Text style={styles.navTitle}>Club Home</Text>
        <View style={{ width: 60 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>

        {/* ── SECTION A: Header ── */}
        <View style={styles.headerCard}>
          <View style={styles.headerTop}>
            <Text style={[styles.orgName, { color: c.text }]} numberOfLines={1}>
              {org?.name ?? 'Your Club'}
            </Text>
            <View style={[styles.typeBadge, { backgroundColor: c.accent + '18', borderColor: c.accent + '40' }]}>
              <Text style={styles.typeBadgeText}>{orgTypeEmoji} {orgTypeLabel}</Text>
            </View>
          </View>
          <View style={styles.rolePillRow}>
            <View style={[styles.rolePill, { backgroundColor: c.surfaceVariant ?? c.surface }]}>
              <Text style={[styles.rolePillText, { color: c.textSecondary }]}>
                {ROLE_LABELS[myRole!]}
              </Text>
            </View>
            {org?.setup_complete ? null : (
              <View style={[styles.setupBadge, { backgroundColor: '#f59e0b20', borderColor: '#f59e0b40' }]}>
                <Text style={{ fontSize: 11, color: '#f59e0b', fontFamily: TYPO.body.semibold }}>
                  ⚠ Setup Incomplete
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── SECTION B: Org Strength ── */}
        <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Org Strength</Text>
        <View style={[styles.strengthCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          {eligibleCount === 0 ? (
            <View style={styles.strengthEmpty}>
              <Text style={[styles.strengthEmptyTitle, { color: c.text }]}>No data yet</Text>
              <Text style={[styles.strengthEmptySub, { color: c.textSecondary }]}>
                Members must complete onboarding and log strength scores to appear here.
              </Text>
            </View>
          ) : (
            <View style={styles.strengthRow}>
              <View style={styles.strengthStat}>
                <Text style={[styles.strengthValue, { color: c.text }]}>
                  {Math.round(avgStrength)}
                </Text>
                <Text style={[styles.strengthUnit, { color: c.textSecondary }]}>avg kg</Text>
              </View>
              <View style={styles.strengthDivider} />
              <View style={styles.strengthStat}>
                <Text style={[styles.strengthValue, { color: c.text }]}>
                  {eligibleCount}
                </Text>
                <Text style={[styles.strengthUnit, { color: c.textSecondary }]}>ranked</Text>
              </View>
              {activeMemberCount > 0 && (
                <>
                  <View style={styles.strengthDivider} />
                  <View style={styles.strengthStat}>
                    <Text style={[styles.strengthValue, { color: c.text }]}>
                      {activeMemberCount}
                    </Text>
                    <Text style={[styles.strengthUnit, { color: c.textSecondary }]}>members</Text>
                  </View>
                </>
              )}
            </View>
          )}
        </View>

        {/* ── SECTION D: First Win State (rendered before Quick Actions for visual priority) ── */}
        {activeMemberCount === 0 ? (
          <View style={[styles.firstWinBanner, { backgroundColor: c.accent + '12', borderColor: c.accent + '30' }]}>
            <Text style={[styles.firstWinTitle, { color: c.text }]}>
              Invite your team to get started
            </Text>
            <Text style={[styles.firstWinSub, { color: c.textSecondary }]}>
              Once athletes join and complete their profile, they'll appear on your leaderboard.
            </Text>
            {isAdminOrOwner && (
              <Pressable
                style={[styles.btnPrimary, { marginTop: 12 }]}
                onPress={() =>
                  router.push({
                    pathname: '/org-roster' as any,
                    params: { orgId: orgId! },
                  })
                }
              >
                <Text style={styles.btnPrimaryText}>Invite Athletes →</Text>
              </Pressable>
            )}
          </View>
        ) : (
          <View style={[styles.activeTeamBanner, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.activeTeamText, { color: c.text }]}>
              ✅ Your team is active · {activeMemberCount} member{activeMemberCount !== 1 ? 's' : ''}
            </Text>
            <Pressable
              onPress={() =>
                router.push({
                  pathname: '/org-leaderboard' as any,
                  params: { orgId: orgId! },
                })
              }
            >
              <Text style={[styles.leaderboardCTA, { color: c.accent }]}>
                View Leaderboard →
              </Text>
            </Pressable>
          </View>
        )}

        {/* ── SECTION C: Quick Actions ── */}
        <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>Quick Actions</Text>
        <View style={styles.actionsGrid}>

          {/* Invite Athletes — owner/admin only */}
          {isAdminOrOwner && (
            <Pressable
              style={[styles.actionCard, { backgroundColor: c.surface, borderColor: c.border }]}
              onPress={() =>
                router.push({
                  pathname: '/org-roster' as any,
                  params: { orgId: orgId! },
                })
              }
            >
              <Text style={styles.actionEmoji}>📩</Text>
              <Text style={[styles.actionLabel, { color: c.text }]}>Invite Athletes</Text>
            </Pressable>
          )}

          {/* View Leaderboard */}
          <Pressable
            style={[styles.actionCard, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={() =>
              router.push({
                pathname: '/org-leaderboard' as any,
                params: { orgId: orgId! },
              })
            }
          >
            <Text style={styles.actionEmoji}>🏆</Text>
            <Text style={[styles.actionLabel, { color: c.text }]}>Leaderboard</Text>
          </Pressable>

          {/* Manage Roster — owner/admin full access, coach read-only */}
          <Pressable
            style={[styles.actionCard, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={() =>
              router.push({
                pathname: '/org-roster' as any,
                params: { orgId: orgId! },
              })
            }
          >
            <Text style={styles.actionEmoji}>👥</Text>
            <Text style={[styles.actionLabel, { color: c.text }]}>
              {isCoach ? 'View Roster' : 'Manage Roster'}
            </Text>
          </Pressable>

          {/* View / Edit Setup — owner/admin only */}
          {isAdminOrOwner && (
            <Pressable
              style={[styles.actionCard, { backgroundColor: c.surface, borderColor: c.border }]}
              onPress={() =>
                router.push({
                  pathname: '/club-setup' as any,
                  params: { orgId: orgId! },
                })
              }
            >
              <Text style={styles.actionEmoji}>⚙️</Text>
              <Text style={[styles.actionLabel, { color: c.text }]}>Club Setup</Text>
            </Pressable>
          )}

          {/* Compare Schools — only shown for school orgs */}
          {orgTypeFull === 'school' && (
            <Pressable
              style={[styles.actionCard, { backgroundColor: c.surface, borderColor: c.border }]}
              onPress={() =>
                router.push({
                  pathname: '/org-comparison' as any,
                  params: { orgId: orgId! },
                })
              }
            >
              <Text style={styles.actionEmoji}>🏫</Text>
              <Text style={[styles.actionLabel, { color: c.text }]}>Compare Schools</Text>
            </Pressable>
          )}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function makeStyles(c: any) {
  return StyleSheet.create({
    centered: {
      flex: 1,
      backgroundColor: c.background,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
    },
    noOrgEmoji: {
      fontSize: 48,
      marginBottom: 16,
    },
    heading: {
      fontSize: 22,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    subText: {
      fontSize: 15,
      color: c.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 24,
    },
    btnPrimary: {
      backgroundColor: c.accent,
      borderRadius: 6,
      paddingVertical: 14,
      paddingHorizontal: 28,
      alignItems: 'center',
    },
    btnPrimaryText: {
      color: c.accentForeground,
      fontFamily: TYPO.heading.bold,
      fontSize: 16,
    },
    btnSecondary: {
      borderRadius: 6,
      borderWidth: 1.5,
      borderColor: c.border,
      paddingVertical: 12,
      paddingHorizontal: 24,
      alignItems: 'center',
    },
    btnSecondaryText: {
      color: c.text,
      fontFamily: TYPO.body.regular,
      fontSize: 15,
    },
    navRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    backBtn: { width: 60 },
    backBtnText: {
      color: c.accent,
      fontSize: 15,
      fontFamily: TYPO.body.regular,
    },
    navTitle: {
      fontSize: 17,
      fontFamily: TYPO.heading.bold,
      color: c.text,
      textAlign: 'center',
    },
    content: {
      padding: 16,
      paddingBottom: 32,
    },
    // Section A
    headerCard: {
      marginBottom: 24,
    },
    headerTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 8,
    },
    orgName: {
      fontSize: 24,
      fontFamily: TYPO.heading.bold,
      flex: 1,
    },
    typeBadge: {
      borderRadius: 20,
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 4,
    },
    typeBadgeText: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
      color: '#888',
    },
    rolePillRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    rolePill: {
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 3,
    },
    rolePillText: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
    },
    setupBadge: {
      borderRadius: 12,
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 3,
    },
    // Section B
    sectionLabel: {
      fontSize: 12,
      fontFamily: TYPO.body.semibold,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginBottom: 10,
    },
    strengthCard: {
      borderRadius: 8,
      borderWidth: 1,
      padding: 16,
      marginBottom: 20,
    },
    strengthEmpty: {
      alignItems: 'center',
      paddingVertical: 8,
    },
    strengthEmptyTitle: {
      fontSize: 15,
      fontFamily: TYPO.body.semibold,
      marginBottom: 6,
    },
    strengthEmptySub: {
      fontSize: 13,
      textAlign: 'center',
      lineHeight: 18,
    },
    strengthRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-around',
    },
    strengthStat: {
      alignItems: 'center',
    },
    strengthValue: {
      fontSize: 28,
      fontFamily: TYPO.heading.bold,
      lineHeight: 34,
    },
    strengthUnit: {
      fontSize: 11,
      fontFamily: TYPO.body.semibold,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 2,
    },
    strengthDivider: {
      width: 1,
      height: 32,
      backgroundColor: '#33333330',
    },
    // Section D
    firstWinBanner: {
      borderRadius: 8,
      borderWidth: 1,
      padding: 16,
      marginBottom: 20,
    },
    firstWinTitle: {
      fontSize: 16,
      fontFamily: TYPO.heading.bold,
      marginBottom: 6,
    },
    firstWinSub: {
      fontSize: 13,
      lineHeight: 18,
    },
    activeTeamBanner: {
      borderRadius: 8,
      borderWidth: 1,
      padding: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
      flexWrap: 'wrap',
      gap: 8,
    },
    activeTeamText: {
      fontSize: 14,
      fontFamily: TYPO.body.semibold,
    },
    leaderboardCTA: {
      fontSize: 14,
      fontFamily: TYPO.body.semibold,
    },
    // Section C
    actionsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      marginBottom: 8,
    },
    actionCard: {
      borderRadius: 8,
      borderWidth: 1,
      padding: 16,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '45%',
      flex: 1,
      gap: 8,
    },
    actionEmoji: {
      fontSize: 28,
    },
    actionLabel: {
      fontSize: 13,
      fontFamily: TYPO.body.semibold,
      textAlign: 'center',
    },
  });
}
